import {IncludeFragment} from '@github-ui/include-fragment-react'
import {testIdProps} from '@github-ui/test-id-props'
import {
  ArchiveIcon,
  BookIcon,
  ChecklistIcon,
  CommentIcon,
  CopyIcon,
  DuplicateIcon,
  GearIcon,
  GraphIcon,
  type Icon,
  KebabHorizontalIcon,
  RocketIcon,
  SidebarExpandIcon,
  WorkflowIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, IconButton, Portal, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type ReactNode, useCallback, useMemo, useRef, useState} from 'react'

import {
  CopyAsTemplate,
  CopyProject,
  InsightsViewOpen,
  ProjectDescriptionShow,
  ProjectDescriptionSidePanelUI,
  ProjectLinkChangelog,
  ProjectLinkDocs,
  ProjectLinkFeedback,
  ProjectTopMenuUI,
  UseThisTemplateUI,
  WorkflowsOpen,
} from '../../api/stats/contracts'
import {getInitialState} from '../../helpers/initial-state'
import {fetchJSONIslandData} from '../../helpers/json-island'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useSidePanel} from '../../hooks/use-side-panel'
import {Link} from '../../router'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {PROJECT_ARCHIVE_ROUTE, PROJECT_SETTINGS_ROUTE, PROJECT_WORKFLOWS_ROUTE} from '../../routes'
import {useCharts} from '../../state-providers/charts/use-charts'
import {useProjectNumber} from '../../state-providers/memex/use-project-number'
import {useProjectState} from '../../state-providers/memex/use-project-state'
import {useProjectTemplateId} from '../../state-providers/memex/use-project-template-id'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {TopBarResources} from '../../strings'
import {FeedbackDialog, type FeedbackDialogRef} from '../feedback-dialog'
import {PresenceAvatars} from '../presence-avatars'
import styles from './index.module.css'
import {LatestStatusUpdate} from './latest-status-update'

const InsightsNavigationButton = memo(function InsightsNavigationButton() {
  const {getChartLinkTo} = useCharts()
  const {postStats} = usePostStats()
  const postInsightsViewOpenStats = useCallback(() => {
    postStats({name: InsightsViewOpen})
  }, [postStats])

  const buttonLabel = 'Insights'
  const icon = GraphIcon
  const url = getChartLinkTo(0).url

  const hideList = ['hide-md', 'hide-lg', 'hide-xl']
  const hideSm = 'hide-sm'

  const commonProps = {
    to: url,
    onClick: postInsightsViewOpenStats,
    as: Link,
  }

  return (
    <>
      {/* Small viewport version - IconButton */}
      <IconButton
        {...commonProps}
        className={clsx(styles.Button_1, hideList)}
        icon={icon}
        aria-label={buttonLabel}
        {...testIdProps('project-insights-button-small')}
      />
      {/* Large viewport version - Button with text */}
      <Button
        {...commonProps}
        className={clsx(styles.Button_1, hideSm)}
        leadingVisual={icon}
        {...testIdProps('project-insights-button')}
      >
        {buttonLabel}
      </Button>
    </>
  )
})

const WorkflowsNavigationButton = memo(function WorkflowsNavigationButton() {
  const {enabledWorkflowsCount} = useWorkflows()
  const projectRouteParams = useProjectRouteParams()
  const {postStats} = usePostStats()
  const postWorkflowsViewOpenStats = useCallback(() => {
    postStats({name: WorkflowsOpen})
  }, [postStats])

  const displayCount = enabledWorkflowsCount > 999 ? '999+' : enabledWorkflowsCount
  const ariaLabel = enabledWorkflowsCount > 0 ? `Workflows (${enabledWorkflowsCount} enabled)` : 'Workflows'

  return (
    <Tooltip text={ariaLabel} type="label" direction="sw">
      <Button
        to={PROJECT_WORKFLOWS_ROUTE.generatePath(projectRouteParams)}
        onClick={postWorkflowsViewOpenStats}
        as={Link}
        className={styles.Button_1}
        count={enabledWorkflowsCount > 0 ? displayCount : undefined}
        leadingVisual={WorkflowIcon}
      >
        <span className="hide-sm">Workflows</span>
      </Button>
    </Tooltip>
  )
})

const ViewRecapButton = memo(function ViewRecapButton() {
  const {openPaneRecap} = useSidePanel()

  const buttonLabel = TopBarResources.viewRecapButton
  const icon = ChecklistIcon

  const hideList = ['hide-md', 'hide-lg', 'hide-xl']
  const hideSm = 'hide-sm'

  const commonProps = {
    onClick: () => openPaneRecap(),
  }

  return (
    <>
      {/* Small viewport version - IconButton with Tooltip */}
      <Tooltip text={buttonLabel} type="label" direction="sw">
        <IconButton {...commonProps} className={clsx(styles.Button_1, hideList)} icon={icon} aria-label={buttonLabel} />
      </Tooltip>
      {/* Large viewport version - Button with text */}
      <Button {...commonProps} className={clsx(styles.Button_1, hideSm)} leadingVisual={icon}>
        {buttonLabel}
      </Button>
    </>
  )
})

const ProjectDetailsButton = memo(function ProjectDescriptionButton() {
  const {openPaneInfo} = useSidePanel()
  const {postStats} = usePostStats()

  const contentRef = useRef<HTMLButtonElement>(null)

  return (
    <IconButton
      ref={contentRef}
      key="project-details-button"
      onClick={() => {
        openPaneInfo(undefined, () => {
          // Return focus to the button when the panel is closed
          contentRef.current?.focus()
        })

        postStats({
          name: ProjectDescriptionShow,
          ui: ProjectDescriptionSidePanelUI,
        })
      }}
      className={styles.Button_1}
      icon={SidebarExpandIcon}
      aria-label={TopBarResources.projectDetailsButton}
      {...testIdProps('project-memex-info-button')}
    />
  )
})

const SettingsOverflowMenu = memo(function SettingsOverflowMenu({
  menuButtonRef,
  openFeedbackDialog,
}: {
  menuButtonRef: React.RefObject<HTMLButtonElement>
  openFeedbackDialog: () => void
}) {
  const {projectNumber} = useProjectNumber()
  const {feedbackLink, copyProjectPartialUrl} = getInitialState()
  const projectRouteParams = useProjectRouteParams()
  const [isOpen, setIsOpen] = useState(false)
  const {postStats} = usePostStats()

  const {hasWritePermissions, isLoggedIn, canCopyAsTemplate} = ViewerPrivileges()

  const toggleIsOpen = useCallback(() => {
    setIsOpen(s => !s)
  }, [])

  const openFeedback = useCallback(() => {
    toggleIsOpen()
    postStats({name: ProjectLinkFeedback, ui: ProjectTopMenuUI})
  }, [postStats, toggleIsOpen])

  const openDocs = useCallback(() => {
    toggleIsOpen()
    postStats({name: ProjectLinkDocs, ui: ProjectTopMenuUI})
  }, [postStats, toggleIsOpen])

  const openChangelog = useCallback(() => {
    toggleIsOpen()
    postStats({name: ProjectLinkChangelog, ui: ProjectTopMenuUI})
  }, [postStats, toggleIsOpen])

  const postMakeCopyStats = useCallback(() => {
    postStats({
      name: CopyProject,
      ui: ProjectTopMenuUI,
    })
  }, [postStats])

  const postCopyAsTemplateStats = useCallback(() => {
    postStats({
      name: CopyAsTemplate,
      ui: ProjectTopMenuUI,
    })
  }, [postStats])

  const showCopyAsTemplate = isLoggedIn && canCopyAsTemplate

  const menuItems = useMemo(() => {
    const items: Array<ReactNode> = []

    if (hasWritePermissions) {
      items.push(
        <InternalLink
          key="archive"
          to={PROJECT_ARCHIVE_ROUTE.generatePath(projectRouteParams)}
          icon={ArchiveIcon}
          text="Archived items"
          onClick={toggleIsOpen}
          testId="archive-navigation-button"
        />,
        <InternalLink
          key="settings"
          to={PROJECT_SETTINGS_ROUTE.generatePath(projectRouteParams)}
          icon={GearIcon}
          text="Settings"
          onClick={toggleIsOpen}
          testId="project-settings-button"
        />,
      )
    }

    if (isLoggedIn) {
      items.push(
        <CopyDialogAction
          dialogId={`copy-project-dialog-${projectNumber}`}
          icon={CopyIcon}
          id={`topmenu-copy-project-dialog-${projectNumber}`}
          key="topMenuCopyProjectAsTemplateButton"
          onSelect={postMakeCopyStats}
          {...testIdProps('copy-project-button')}
        >
          Make a copy
        </CopyDialogAction>,
      )
    }

    if (showCopyAsTemplate) {
      items.push(
        <CopyDialogAction
          dialogId={`copy-as-template-dialog-${projectNumber}`}
          icon={DuplicateIcon}
          id={`topmenu-copy-as-template-dialog-${projectNumber}`}
          key="topMenuCopyAsTemplateButton"
          onSelect={postCopyAsTemplateStats}
          {...testIdProps('copy-as-template-button')}
        >
          Copy as template
        </CopyDialogAction>,
      )
    }

    if (items.length) {
      items.push(<ActionMenu.Divider key="divider-write-permissions" />)
    }

    const githubRuntime = fetchJSONIslandData('github-runtime') ?? 'dotcom'
    const githubVersionNumber = fetchJSONIslandData('github-version-number')
    const versionNumber = githubVersionNumber === 'unknown' ? 'latest' : githubVersionNumber
    const docsLink =
      githubRuntime === 'enterprise'
        ? `https://docs.github.com/enterprise-server@${versionNumber}/issues/planning-and-tracking-with-projects`
        : 'https://docs.github.com/issues/planning-and-tracking-with-projects'

    const isMultiTenantEnterprise = fetchJSONIslandData('is-github-multi-tenant-enterprise') ?? false
    // If the runtime is dotcom and not a proxima tenant, we show the feedback dialog
    const showFeedbackDialog = !isMultiTenantEnterprise && githubRuntime === 'dotcom'

    items.push(
      <ActionList.Group key="title-github-projects" variant="subtle">
        <ActionList.GroupHeading>GitHub Projects</ActionList.GroupHeading>
        <ExternalLink
          key="new"
          href="https://github.blog/changelog?label=projects-and-issues"
          icon={RocketIcon}
          text="What’s new"
          onClick={openChangelog}
          testId="whats-new-link"
        />
        {showFeedbackDialog ? (
          <FeedbackDialogAction onSelect={openFeedbackDialog} />
        ) : (
          <ExternalLink
            key="feedback"
            href={feedbackLink}
            icon={CommentIcon}
            text="Give feedback"
            onClick={openFeedback}
            testId="feedback-link"
          />
        )}
        <ExternalLink
          key="docs"
          href={docsLink}
          icon={BookIcon}
          text="GitHub Docs"
          onClick={openDocs}
          testId="docs-link"
        />
      </ActionList.Group>,
    )

    return items
  }, [
    hasWritePermissions,
    isLoggedIn,
    showCopyAsTemplate,
    openChangelog,
    openFeedbackDialog,
    feedbackLink,
    openFeedback,
    openDocs,
    toggleIsOpen,
    projectNumber,
    projectRouteParams,
    postMakeCopyStats,
    postCopyAsTemplateStats,
  ])

  return (
    <>
      <IconButton
        ref={menuButtonRef}
        {...testIdProps('project-menu-button')}
        className={styles.Button_1}
        onClick={() => setIsOpen(s => !s)}
        icon={KebabHorizontalIcon}
        aria-label={TopBarResources.viewMoreOptions}
      />
      <ActionMenu anchorRef={menuButtonRef} open={isOpen} onOpenChange={setIsOpen} key="project-menu">
        <ActionMenu.Overlay>
          <ActionList>{menuItems}</ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <Portal>
        <div>
          {isLoggedIn && <IncludeFragment src={encodeURI(copyProjectPartialUrl)} />}
          {showCopyAsTemplate && <IncludeFragment src={encodeURI(`${copyProjectPartialUrl}?copy_as_template=true`)} />}
        </div>
      </Portal>
    </>
  )
})

const ProjectNavigationButtons = memo(function ProjectNavigationButtons({
  menuButtonRef,
  isProjectPath,
  openFeedbackDialog,
}: {
  menuButtonRef: React.RefObject<HTMLButtonElement>
  isProjectPath: boolean
  openFeedbackDialog: () => void
}) {
  const {memex_automation_enabled, scribe_reporting_enabled} = useEnabledFeatures()
  const {hasWritePermissions} = ViewerPrivileges()
  const isProjectDescriptionButtonVisible = isProjectPath

  const buttonGroupOne = [<InsightsNavigationButton key="insights-navigation-button" />]

  if (scribe_reporting_enabled) {
    buttonGroupOne.push(<ViewRecapButton key="view-recap-button" />)
  }

  if (memex_automation_enabled && hasWritePermissions) {
    buttonGroupOne.push(<WorkflowsNavigationButton key="workflows-navigation-button" />)
  }

  const buttonGroupTwo = [
    <SettingsOverflowMenu
      key="settings-overflow-menu"
      menuButtonRef={menuButtonRef}
      openFeedbackDialog={openFeedbackDialog}
    />,
  ]

  if (isProjectDescriptionButtonVisible) buttonGroupTwo.unshift(<ProjectDetailsButton key="project-details-button" />)

  return (
    <>
      <ButtonGroup className={styles.ButtonGroup}>{buttonGroupOne}</ButtonGroup>
      <ButtonGroup className={styles.ButtonGroup}>{buttonGroupTwo}</ButtonGroup>
    </>
  )
})

// Abbreviated version of the "Make a copy" form in settings, with static defaults
const UseTemplateButton = memo(function UseTemplateButton() {
  const {copyProjectPartialUrl} = getInitialState()
  const {isLoggedIn} = ViewerPrivileges()
  const {projectNumber} = useProjectNumber()
  const {postStats} = usePostStats()
  const projectTemplateId = useProjectTemplateId()
  const postUseThisTemplateStats = useCallback(() => {
    postStats({
      name: CopyProject,
      ui: UseThisTemplateUI,
    })
  }, [postStats])

  return (
    <div {...testIdProps('use-this-template-form')} className={styles.Box}>
      <Button
        size="medium"
        variant="primary"
        {...testIdProps('use-this-template-button')}
        aria-label="Use this template"
        data-show-dialog-id={`copy-from-template-dialog-${projectNumber}`}
        onClick={postUseThisTemplateStats}
      >
        Use this template
      </Button>
      <Portal>
        <div>
          {isLoggedIn && (
            <IncludeFragment src={encodeURI(`${copyProjectPartialUrl}?template_id=${projectTemplateId}`)} />
          )}
        </div>
      </Portal>
    </div>
  )
})

type LinkProps = {
  icon: Icon
  text: string
  onClick: () => void
  testId: string
}
type InternalLinkProps = LinkProps & {to: string}
type ExternalLinkProps = LinkProps & {href: string}

const ExternalLink = ({text, icon: LinkIcon, testId, onClick, href}: ExternalLinkProps) => (
  <ActionList.LinkItem role="menuitem" href={href} target="_blank" onClick={onClick} {...testIdProps(testId)}>
    <ActionList.LeadingVisual>
      <LinkIcon />
    </ActionList.LeadingVisual>
    {text}
  </ActionList.LinkItem>
)

const InternalLink = ({text, icon: LinkIcon, testId, onClick, to}: InternalLinkProps) => (
  <ActionList.LinkItem role="menuitem" as={Link} to={to} onClick={onClick} {...testIdProps(testId)}>
    <ActionList.LeadingVisual>
      <LinkIcon />
    </ActionList.LeadingVisual>
    {text}
  </ActionList.LinkItem>
)

type CopyDialogActionProps = {
  children: React.ReactNode
  id: string
  dialogId: string
  icon: Icon
  onSelect: () => void
}

/**
 * The structure of this component addresses bug found in
 * https://github.com/github/projects-platform/issues/2797#issuecomment-2652433827
 */
const CopyDialogAction = ({children, dialogId, icon: ActionIcon, ...other}: CopyDialogActionProps) => (
  <ActionList.Item role="menuitem" onKeyPress={undefined}>
    <ActionList.LeadingVisual>
      <ActionIcon />
    </ActionList.LeadingVisual>
    <button data-show-dialog-id={dialogId} className={styles.Box_1} {...other}>
      {children}
    </button>
  </ActionList.Item>
)

type FeedbackDialogActionProps = {
  onSelect: () => void
}

const FeedbackDialogAction = ({onSelect}: FeedbackDialogActionProps) => (
  <ActionList.Item role="menuitem" onSelect={onSelect}>
    <ActionList.LeadingVisual>
      <CommentIcon />
    </ActionList.LeadingVisual>
    Give feedback
  </ActionList.Item>
)

export const TopBar: React.FC<{children: React.ReactNode; isProjectPath?: boolean}> = memo(function TopBar({
  children,
  isProjectPath = false,
}) {
  const {isTemplate} = useProjectState()
  const {isOrganization} = getInitialState()

  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const feedbackDialogRef = useRef<FeedbackDialogRef>(null)
  const openFeedbackDialog = useCallback(() => feedbackDialogRef.current?.openDialog(), [])

  return (
    <>
      <div
        role="navigation"
        aria-label="Project"
        className={clsx(styles.topBar, {[styles.topBarWithBorder]: !isProjectPath})}
        {...testIdProps('top-bar')}
      >
        {children}
        <div style={{flex: 1}} />
        <div className={styles.topBarActions}>
          {isProjectPath && !isTemplate && <LatestStatusUpdate />}
          <PresenceAvatars />
          <ProjectNavigationButtons
            menuButtonRef={menuButtonRef}
            isProjectPath={isProjectPath}
            openFeedbackDialog={openFeedbackDialog}
          />

          {isOrganization && isTemplate ? <UseTemplateButton /> : null}
        </div>
      </div>
      <FeedbackDialog ref={feedbackDialogRef} returnFocusRef={menuButtonRef} />
    </>
  )
})

try{ InsightsNavigationButton.displayName ||= 'InsightsNavigationButton' } catch {}
try{ WorkflowsNavigationButton.displayName ||= 'WorkflowsNavigationButton' } catch {}
try{ ViewRecapButton.displayName ||= 'ViewRecapButton' } catch {}
try{ ProjectDetailsButton.displayName ||= 'ProjectDetailsButton' } catch {}
try{ SettingsOverflowMenu.displayName ||= 'SettingsOverflowMenu' } catch {}
try{ ProjectNavigationButtons.displayName ||= 'ProjectNavigationButtons' } catch {}
try{ UseTemplateButton.displayName ||= 'UseTemplateButton' } catch {}
try{ ExternalLink.displayName ||= 'ExternalLink' } catch {}
try{ InternalLink.displayName ||= 'InternalLink' } catch {}
try{ CopyDialogAction.displayName ||= 'CopyDialogAction' } catch {}
try{ FeedbackDialogAction.displayName ||= 'FeedbackDialogAction' } catch {}
try{ TopBar.displayName ||= 'TopBar' } catch {}